import axios from 'axios';
import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { searchProjects, submitProject, joinProject } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Search, Building2, MapPin, Plus, ArrowRight, 
  CheckCircle, Loader2, Users, Home, Send,
  FileText, AlertCircle, ChevronRight
} from 'lucide-react';

const PENDING_KEY = 'fbc_pending_project';

const INDIAN_STATES = [
  "Maharashtra", "Karnataka", "Tamil Nadu", "Delhi NCR", "Gujarat", 
  "Telangana", "West Bengal", "Uttar Pradesh", "Rajasthan", "Kerala",
  "Haryana", "Punjab", "Madhya Pradesh", "Bihar", "Andhra Pradesh"
];

export default function FindAddProject({ 
  isOpen, 
  onClose, 
  onRequireAuth,  // Callback when user needs to login/register
  embedded = false,  // If true, renders inline instead of dialog
  onResume,          // Called when details saved before sign-up are restored
  prefill = null     // { name } from a "Request your project" button elsewhere
}) {
  const { user } = useAuth();
  const [step, setStep] = useState('search'); // search, results, add, success
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [flatNumber, setFlatNumber] = useState('');
  const [dupes, setDupes] = useState([]);
  const dupeOk = useRef(false);
  
  // Form for adding new project
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    city: '',
    state: '',
    developer_name: '',
    rera_id: '',
    rera_portal_url: '',  // NEW: RERA portal URL for easy verification
    total_apartments: '',
    total_wings: '',      // NEW: Number of wings/towers
    apartment_types: '',
    additional_info: ''
  });

  const [submittedId, setSubmittedId] = useState(null);
  // Details typed before sign-up come back once the visitor is signed in.
  useEffect(() => {
    const saved = user && sessionStorage.getItem(PENDING_KEY);
    if (!saved) return;
    sessionStorage.removeItem(PENDING_KEY);
    try { setFormData((f) => ({ ...f, ...JSON.parse(saved) })); setStep('add'); onResume?.(); } catch { /* ignore bad data */ }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen || !prefill) return;
    dupeOk.current = false;
    setSelectedProject(null);
    setFormData((f) => ({ ...f, name: prefill.name || f.name }));
    setStep('add');
  }, [isOpen, prefill]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a project name or location');
      return;
    }
    
    setLoading(true);
    try {
      const results = await searchProjects({ q: searchQuery });
      setSearchResults(results);
      setStep('results');
    } catch (err) {
      console.error(err);
      toast.error('Failed to search projects');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinProject = async () => {
    if (!user) {
      onRequireAuth?.('login');
      return;
    }
    
    if (!selectedProject) return;
    
    setLoading(true);
    try {
      await joinProject(selectedProject.id, flatNumber || null);
      toast.success(`You've joined ${selectedProject.name}!`);
      setStep('success');
    } catch (err) {
      console.error(err);
      toast.error('Failed to join project');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitProject = async (e) => {
    e.preventDefault();
    
    if (!user) {
      sessionStorage.setItem(PENDING_KEY, JSON.stringify(formData));
      onRequireAuth?.('register');
      return;
    }
    
    if (!formData.name || !formData.location || !formData.city || !formData.state) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Search-first: show likely existing matches once; "None of these" continues to registration.
    if (!dupeOk.current) {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/projects/submissions/similar`,
          { params: { name: formData.name, city: formData.city, rera_id: formData.rera_id || undefined } });
        if (data.matches?.length) { setDupes(data.matches); setStep('dupes'); return; }
      } catch { /* never block registration on the check */ }
    }

    setLoading(true);
    try {
      const submission = {
        ...formData,
        total_apartments: formData.total_apartments ? parseInt(formData.total_apartments) : null,
        total_wings: formData.total_wings ? parseInt(formData.total_wings) : null
      };
      const created = await submitProject(submission);
      setSubmittedId(created?.id || null);
      toast.success('Request received. FBC will check it against the RERA record and let you know.');
      setStep('success');
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit project');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetFlow = () => {
    dupeOk.current = false;
    setSubmittedId(null);
    setStep('search');
    setSearchQuery('');
    setSearchResults([]);
    setSelectedProject(null);
    setFlatNumber('');
    setFormData({
      name: '',
      location: '',
      city: '',
      state: '',
      developer_name: '',
      rera_id: '',
      rera_portal_url: '',
      total_apartments: '',
      total_wings: '',
      apartment_types: '',
      additional_info: ''
    });
  };

  const renderContent = () => (
    <div className="space-y-6">
      <AnimatePresence mode="wait">
        {step === 'search' && (
          <motion.div
            key="search"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-6"
          >
            <div className="text-center space-y-2">
              <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
                <Building2 className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold">Find Your Project</h3>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                Search for your property project to connect with other buyers and track updates
              </p>
            </div>
            
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Enter project name, location, or developer..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                  data-testid="project-search-input"
                />
              </div>
              <Button onClick={handleSearch} disabled={loading} data-testid="project-search-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
              </Button>
            </div>
            
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-2">Can't find your project? It can still be part of FBC.</p>
              <Button 
                variant="outline" 
                onClick={() => setStep('add')}
                data-testid="add-new-project-btn"
              >
                <Plus className="h-4 w-4 mr-2" /> Request your project
              </Button>
            </div>
          </motion.div>
        )}

        {step === 'results' && (
          <motion.div
            key="results"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-4"
          >
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={() => setStep('search')}>
                Back to Search
              </Button>
              <Badge variant="secondary">{searchResults.length} results</Badge>
            </div>
            
            {searchResults.length > 0 ? (
              <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                {searchResults.map((project) => (
                  <Card 
                    key={project.id} 
                    className={`cursor-pointer transition-all hover:shadow-md ${selectedProject?.id === project.id ? 'ring-2 ring-primary' : ''}`}
                    onClick={() => setSelectedProject(project)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium">{project.name}</h4>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                            <MapPin className="h-3 w-3" />
                            {project.location}
                          </div>
                          {project.developer_name && (
                            <p className="text-xs text-muted-foreground mt-1">
                              By {project.developer_name}
                            </p>
                          )}
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground mb-1">No projects found matching "{searchQuery}"</p>
                <p className="text-sm mb-4">Your project can still be part of FBC. Ask us to open it and bring your fellow buyers along.</p>
                <Button onClick={() => { updateField('name', searchQuery); setStep('add'); }} data-testid="request-this-project-btn">
                  <Plus className="h-4 w-4 mr-2" /> Request this project
                </Button>
              </div>
            )}
            
            {selectedProject && (
              <div className="pt-4 border-t space-y-3">
                <h4 className="font-medium">Join {selectedProject.name}</h4>
                <div className="space-y-2">
                  <Label htmlFor="flat-number">Your Flat/Unit Number (Optional)</Label>
                  <Input
                    id="flat-number"
                    placeholder="e.g., A-1204, Tower B - 503"
                    value={flatNumber}
                    onChange={(e) => setFlatNumber(e.target.value)}
                  />
                </div>
                <Button 
                  className="w-full" 
                  onClick={handleJoinProject} 
                  disabled={loading}
                  data-testid="join-project-btn"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Users className="h-4 w-4 mr-2" />}
                  {user ? 'Join This Project' : 'Sign In to Join'}
                </Button>
              </div>
            )}
            
            <div className="text-center pt-2">
              <Button variant="link" onClick={() => setStep('add')}>
                Project not listed? Request it here
              </Button>
            </div>
          </motion.div>
        )}

        {step === 'add' && (
          <motion.div
            key="add"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-4"
          >
            <Button variant="ghost" size="sm" onClick={() => setStep('search')}>
              Back to Search
            </Button>
            
            <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <div className="flex gap-3">
                <FileText className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-700">Request your project's community</p>
                  <p className="text-muted-foreground">
                    FBC checks the details against the RERA record and then opens the project. It goes
                    Requested → Under review → Live. You can invite fellow buyers straight away, and use your
                    personal vault, the checklists and the Knowledge Hub while you wait.
                  </p>
                </div>
              </div>
            </div>
            
            <form onSubmit={handleSubmitProject} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="project-name">Project Name *</Label>
                  <Input
                    id="project-name"
                    placeholder="e.g., Sunrise Heights, Green Valley Residency"
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    required
                    data-testid="project-name-input"
                  />
                </div>
                
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="location">Full Address / Location *</Label>
                  <Input
                    id="location"
                    placeholder="e.g., Sector 42, Near Metro Station"
                    value={formData.location}
                    onChange={(e) => updateField('location', e.target.value)}
                    required
                    data-testid="project-location-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    placeholder="e.g., Gurugram, Mumbai, Bangalore"
                    value={formData.city}
                    onChange={(e) => updateField('city', e.target.value)}
                    data-testid="project-city-input"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="state">State *</Label>
                  <Select value={formData.state} onValueChange={(v) => updateField('state', v)}>
                    <SelectTrigger data-testid="project-state-select">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDIAN_STATES.map(state => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="developer">Developer Name</Label>
                  <Input
                    id="developer"
                    placeholder="e.g., DLF, Godrej Properties"
                    value={formData.developer_name}
                    onChange={(e) => updateField('developer_name', e.target.value)}
                    data-testid="project-developer-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="rera">RERA Registration No.</Label>
                  <Input
                    id="rera"
                    placeholder="e.g., RERA123456789"
                    value={formData.rera_id}
                    onChange={(e) => updateField('rera_id', e.target.value)}
                    data-testid="project-rera-input"
                  />
                </div>
                
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="rera-url">
                    RERA Portal URL 
                    <span className="text-muted-foreground text-xs ml-1">(helps us verify faster)</span>
                  </Label>
                  <Input
                    id="rera-url"
                    type="url"
                    placeholder="e.g., https://maharera.mahaonline.gov.in/project/..."
                    value={formData.rera_portal_url}
                    onChange={(e) => updateField('rera_portal_url', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Paste the direct link to your project on the state RERA portal
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="wings">Total Wings/Towers</Label>
                  <Input
                    id="wings"
                    type="number"
                    placeholder="e.g., 13"
                    value={formData.total_wings}
                    onChange={(e) => updateField('total_wings', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="apartments">Total Apartments (approx.)</Label>
                  <Input
                    id="apartments"
                    type="number"
                    placeholder="e.g., 480"
                    value={formData.total_apartments}
                    onChange={(e) => updateField('total_apartments', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="types">Apartment Types</Label>
                  <Input
                    id="types"
                    placeholder="e.g., 2BHK, 3BHK, 4BHK"
                    value={formData.apartment_types}
                    onChange={(e) => updateField('apartment_types', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="additional">Additional Information</Label>
                  <Textarea
                    id="additional"
                    placeholder="Any other details about the project that might help..."
                    value={formData.additional_info}
                    onChange={(e) => updateField('additional_info', e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
                data-testid="submit-project-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                {user ? 'Send request' : 'Sign up to send request'}
              </Button>
            </form>
          </motion.div>
        )}

        {step === 'dupes' && (
          <div className="space-y-3" data-testid="dupe-check">
            <p className="text-sm font-semibold">Is your project one of these?</p>
            <p className="text-xs text-muted-foreground">These look similar to "{formData.name}". Joining an existing project keeps all its buyers together.</p>
            {dupes.map((m) => (
              <div key={m.id} className="flex flex-wrap items-center gap-2 rounded-md border p-2.5 text-sm" data-testid={`dupe-${m.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{m.name}</p>
                  <p className="text-xs text-muted-foreground">{[m.developer_name, m.location, m.city].filter(Boolean).join(' · ')}{m.rera_id ? ` · RERA ${m.rera_id}` : ''}</p>
                </div>
                {m.kind === 'project' ? (
                  <Button size="sm" variant="outline" onClick={() => { setSelectedProject(m); setStep('results'); setSearchResults([m]); }} data-testid={`dupe-join-${m.id}`}>
                    This is my project: request to join
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => { sessionStorage.setItem('fbc_req', m.id); window.dispatchEvent(new CustomEvent('fbc-nav', { detail: 'project-request' })); onClose?.(); }} data-testid={`dupe-support-${m.id}`}>
                    Already requested: join this request
                  </Button>
                )}
              </div>
            ))}
            <div className="flex gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={() => setStep('add')}>Back</Button>
              <Button size="sm" onClick={() => { dupeOk.current = true; setStep('add'); setTimeout(() => document.querySelector('[data-testid="submit-project-btn"]')?.click(), 60); }} data-testid="dupe-none">
                None of these: register new
              </Button>
            </div>
          </div>
        )}

        {step === 'success' && (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center space-y-4 py-6"
          >
            <div className="w-20 h-20 mx-auto bg-green-500/10 rounded-full flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold">{selectedProject ? 'Joined' : 'Request received'}</h3>
            <p className="text-muted-foreground max-w-sm mx-auto">
              {selectedProject 
                ? `You've joined ${selectedProject.name}. Connect with other buyers and stay updated on your project.`
                : 'FBC will check it against the RERA record and email you when it goes live. Meanwhile, invite your fellow buyers and use your vault, the checklists and the Knowledge Hub.'
              }
            </p>
            <div className="flex flex-wrap gap-3 justify-center pt-4">
              <Button variant="outline" onClick={resetFlow}>
                Search Another Project
              </Button>
              {!selectedProject && (
                <Button variant="outline" onClick={() => { window.dispatchEvent(new CustomEvent('fbc-nav', { detail: 'my-submissions' })); onClose?.(); }} data-testid="track-submission-btn">
                  Track it and invite buyers
                </Button>
              )}
              <Button onClick={onClose}>
                <Home className="h-4 w-4 mr-2" /> Go to Dashboard
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  if (embedded) {
    return renderContent();
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            {step === 'add' ? 'Add Your Project' : step === 'success' ? '' : 'Find Your Project'}
          </DialogTitle>
        </DialogHeader>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}

